
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Track scroll position to toggle navbar background
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scrolling when the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const isAboutPage = location.pathname === '/about';

  return (
    <>
      {/* Main Navigation Bar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-16">
          <div className="flex items-center justify-between h-16 md:h-20 lg:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://static.readdy.ai/image/f38dbee0b97d493b71475a98132a7b29/a415b53a125fde8d120dd58a802f1168.png"
                alt="BuildFast - Nigerian Engineers. German Discipline."
                className="h-10 md:h-12 lg:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-8 xl:gap-12">
              <Link
                to="/"
                className={`text-sm tracking-wider uppercase transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-[#0A0A0A] hover:text-[#C9A86C]'
                    : 'text-white hover:text-[#C9A86C]'
                } ${location.pathname === '/' ? 'text-[#C9A86C]' : ''}`}
              >
                Home
              </Link>

              <Link
                to="/about"
                className={`text-sm tracking-wider uppercase transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-[#0A0A0A] hover:text-[#C9A86C]'
                    : 'text-white hover:text-[#C9A86C]'
                } ${isAboutPage ? 'text-[#C9A86C]' : ''}`}
              >
                About
              </Link>

              {isAboutPage ? (
                <Link
                  to="/#projects"
                  className={`text-sm tracking-wider uppercase transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-[#0A0A0A] hover:text-[#C9A86C]'
                      : 'text-white hover:text-[#C9A86C]'
                  }`}
                >
                  Projects
                </Link>
              ) : (
                <a
                  href="#projects"
                  className={`text-sm tracking-wider uppercase transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-[#0A0A0A] hover:text-[#C9A86C]'
                      : 'text-white hover:text-[#C9A86C]'
                  }`}
                >
                  Projects
                </a>
              )}

              {isAboutPage ? (
                <Link
                  to="/#services"
                  className={`text-sm tracking-wider uppercase transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-[#0A0A0A] hover:text-[#C9A86C]'
                      : 'text-white hover:text-[#C9A86C]'
                  }`}
                >
                  Services
                </Link>
              ) : (
                <a
                  href="#services"
                  className={`text-sm tracking-wider uppercase transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-[#0A0A0A] hover:text-[#C9A86C]'
                      : 'text-white hover:text-[#C9A86C]'
                  }`}
                >
                  Services
                </a>
              )}

              {isAboutPage ? (
                <Link
                  to="/#contact"
                  className={`text-sm tracking-wider uppercase transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-[#0A0A0A] hover:text-[#C9A86C]'
                      : 'text-white hover:text-[#C9A86C]'
                  }`}
                >
                  Contact
                </Link>
              ) : (
                <a
                  href="#contact"
                  className={`text-sm tracking-wider uppercase transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-[#0A0A0A] hover:text-[#C9A86C]'
                      : 'text-white hover:text-[#C9A86C]'
                  }`}
                >
                  Contact
                </a>
              )}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-6">
              <a
                href="tel:08145540554"
                className={`hidden md:flex items-center gap-2 transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-[#0A0A0A]/70 hover:text-[#C9A86C]'
                    : 'text-white/70 hover:text-[#C9A86C]'
                }`}
              >
                <i className="ri-phone-line" />
                <span className="text-sm">08145540554</span>
              </a>

              <button className="hidden sm:block group relative px-6 py-3 bg-transparent border border-[#C9A86C] text-[#C9A86C] text-sm font-medium tracking-wide overflow-hidden transition-all duration-500 hover:text-[#0A0A0A] whitespace-nowrap cursor-pointer">
                <span className="relative z-10">Get Quote</span>
                <div className="absolute inset-0 bg-[#C9A86C] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer relative z-[60]"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <span
                  className={`w-6 h-px transition-all duration-300 ${
                    mobileMenuOpen
                      ? 'rotate-45 translate-y-[7px] bg-white'
                      : scrolled
                      ? 'bg-[#0A0A0A]'
                      : 'bg-white'
                  }`}
                />
                <span
                  className={`w-4 h-px bg-[#C9A86C] transition-all duration-300 ${
                    mobileMenuOpen ? 'opacity-0 scale-0' : ''
                  }`}
                />
                <span
                  className={`w-6 h-px transition-all duration-300 ${
                    mobileMenuOpen
                      ? '-rotate-45 -translate-y-[7px] bg-white'
                      : scrolled
                      ? 'bg-[#0A0A0A]'
                      : 'bg-white'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-80 bg-[#0A0A0A] z-50 transform transition-transform duration-500 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-[#C9A86C]/30 to-transparent" />
        <div className="absolute top-20 right-8 w-20 h-20 border border-[#C9A86C]/10 rotate-45" />
        <div className="absolute bottom-32 left-8 w-16 h-16 border border-[#C9A86C]/10 rotate-12" />

        <div className="flex flex-col h-full pt-24 pb-8 px-8">
          {/* Navigation Links */}
          <nav className="flex-1 px-6 py-8">
            <div className="space-y-1">
              <Link
                to="/"
                className={`block py-4 text-white/80 hover:text-[#C9A86C] transition-all duration-300 text-base tracking-wider uppercase border-b border-white/10 cursor-pointer ${
                  mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                } ${location.pathname === '/' ? 'text-[#C9A86C]' : ''}`}
                style={{ transitionDelay: '100ms' }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>

              <Link
                to="/about"
                className={`block py-4 text-white/80 hover:text-[#C9A86C] transition-all duration-300 text-base tracking-wider uppercase border-b border-white/10 cursor-pointer ${
                  mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                } ${isAboutPage ? 'text-[#C9A86C]' : ''}`}
                style={{ transitionDelay: '150ms' }}
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>

              {isAboutPage ? (
                <Link
                  to="/#projects"
                  className={`block py-4 text-white/80 hover:text-[#C9A86C] transition-all duration-300 text-base tracking-wider uppercase border-b border-white/10 cursor-pointer ${
                    mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                  }`}
                  style={{ transitionDelay: '200ms' }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Projects
                </Link>
              ) : (
                <a
                  href="#projects"
                  className={`block py-4 text-white/80 hover:text-[#C9A86C] transition-all duration-300 text-base tracking-wider uppercase border-b border-white/10 cursor-pointer ${
                    mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                  }`}
                  style={{ transitionDelay: '200ms' }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Projects
                </a>
              )}

              {isAboutPage ? (
                <Link
                  to="/#services"
                  className={`block py-4 text-white/80 hover:text-[#C9A86C] transition-all duration-300 text-base tracking-wider uppercase border-b border-white/10 cursor-pointer ${
                    mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                  }`}
                  style={{ transitionDelay: '250ms' }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Services
                </Link>
              ) : (
                <a
                  href="#services"
                  className={`block py-4 text-white/80 hover:text-[#C9A86C] transition-all duration-300 text-base tracking-wider uppercase border-b border-white/10 cursor-pointer ${
                    mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                  }`}
                  style={{ transitionDelay: '250ms' }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Services
                </a>
              )}

              {isAboutPage ? (
                <Link
                  to="/#contact"
                  className={`block py-4 text-white/80 hover:text-[#C9A86C] transition-all duration-300 text-base tracking-wider uppercase border-b border-white/10 cursor-pointer ${
                    mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                  }`}
                  style={{ transitionDelay: '300ms' }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
              ) : (
                <a
                  href="#contact"
                  className={`block py-4 text-white/80 hover:text-[#C9A86C] transition-all duration-300 text-base tracking-wider uppercase border-b border-white/10 cursor-pointer ${
                    mobileMenuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                  }`}
                  style={{ transitionDelay: '300ms' }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </a>
              )}
            </div>
          </nav>

          {/* Bottom Section (Phone, Quote, Social) */}
          <div
            className="space-y-6 transition-all duration-500"
            style={{
              transitionDelay: mobileMenuOpen ? '250ms' : '0ms',
              transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(20px)',
              opacity: mobileMenuOpen ? 1 : 0,
            }}
          >
            <a
              href="tel:08145540554"
              className="flex items-center gap-3 text-white/70 hover:text-[#C9A86C] transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 flex items-center justify-center border border-[#C9A86C]/30 rounded-full">
                <i className="ri-phone-line text-[#C9A86C]" />
              </div>
              <span className="text-sm">08145540554</span>
            </a>

            <button className="w-full group relative py-4 bg-transparent border border-[#C9A86C] text-[#C9A86C] text-sm font-medium tracking-wide overflow-hidden transition-all duration-500 hover:text-[#0A0A0A] whitespace-nowrap cursor-pointer">
              <span className="relative z-10">Get Quote</span>
              <div className="absolute inset-0 bg-[#C9A86C] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
            </button>

            <div className="flex items-center justify-center gap-4 pt-4 border-t border-white/10">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-[#C9A86C] transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-lg" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-[#C9A86C] transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-lg" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-[#C9A86C] transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-line text-lg" />
              </a>
              <a
                href="https://wa.me/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-[#C9A86C] transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
